import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  Block as CancelIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import CancelSaleDialog from "../../components/sales/CancelSaleDialog";
import SaleBillDrawer from "../../components/sales/SaleBillDrawer";
import reportAPI, { SalesReportFilter, SalesReportRow, SalesReportSortField } from "../../services/reportAPI";
import financialYearAPI, {
  FinancialYear,
  formatFinancialYearDisplay,
  indiaFyMeta,
} from "../../services/financialYearAPI";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { userAPI, User } from "../../services/userAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import { billSeqDigits } from "../../utils/billReceipt.helper";
import {
  paymentMethodChipSx,
  paymentMethodLabel,
} from "../../utils/paymentMethodUi";

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

/** Format YYYY-MM-DD as calendar label (no UTC midnight shift). */
const formatYmdLabel = (ymd: string) => {
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return format(new Date(y, m - 1, d), "dd MMM yyyy");
};

/** Always show sale timestamps in Asia/Kolkata. */
const formatIstDateTime = (iso: string) =>
  new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(iso));

const defaultStart = () => indiaFyMeta().startDate;
const defaultEnd = () => {
  const today = format(new Date(), "yyyy-MM-dd");
  const fyEnd = indiaFyMeta().endDate;
  return today < fyEnd ? today : fyEnd;
};

const SalesReportPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { canDelete } = useRBAC();
  const { user } = useAuthStore();
  const role = String(user?.role || "").toUpperCase();
  const isSuperAdmin = role === "SUPER_ADMIN" || role === "SUPERADMIN";
  const isAdmin = role === "ADMIN";
  const canDeleteSale = canDelete("sales");
  const [actionMsg, setActionMsg] = useState<{ type: "success" | "error"; text: string } | null>(
    null
  );
  const [cancelTarget, setCancelTarget] = useState<{
    id: string;
    saleNumber?: string;
    netAmount: number;
    status?: string;
  } | null>(null);
  const [viewSale, setViewSale] = useState<SalesReportRow | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [draft, setDraft] = useState<SalesReportFilter>({
    startDate: defaultStart(),
    endDate: defaultEnd(),
    branchId: isAdmin ? user?.branchId : undefined,
    salesPointId: undefined,
    userId: undefined,
    fyCode: indiaFyMeta().code,
  });
  const [filters, setFilters] = useState<SalesReportFilter>(draft);
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<SalesReportSortField>("createdAt", "desc");

  const { data: financialYears = [] } = useQuery({
    queryKey: ["financialYears"],
    queryFn: () => financialYearAPI.list(),
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branchesAll"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["salesPointsAll"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  const { data: salesPointUsers = [], isFetching: loadingSoldBy } = useQuery({
    queryKey: ["salesReportUsers", draft.salesPointId, draft.branchId],
    queryFn: async (): Promise<User[]> => {
      const spId = draft.salesPointId;
      if (!spId) return [];
      const res = await userAPI.getUsers({
        salesPointId: spId,
        branchId: draft.branchId || (isAdmin ? user?.branchId : undefined) || undefined,
        pageNo: 1,
        pageSize: 200,
        isActive: true,
      });
      // Defense: only users assigned to this sales point
      return (res.users || []).filter((u) => u.salesPointId === spId);
    },
    enabled: Boolean(draft.salesPointId),
  });

  const branchSalesPoints = useMemo(() => {
    const bid = isAdmin ? user?.branchId : draft.branchId;
    if (!bid) return salesPoints;
    return salesPoints.filter((d) => d.branchId === bid);
  }, [salesPoints, draft.branchId, isAdmin, user?.branchId]);

  useEffect(() => {
    if (isAdmin && user?.branchId) {
      setDraft((p) => ({ ...p, branchId: user.branchId }));
    }
  }, [isAdmin, user?.branchId]);

  useEffect(() => {
    // Drop cashier filter when sales point cleared or user left that point
    if (!draft.salesPointId) {
      setDraft((p) => (p.userId ? { ...p, userId: undefined } : p));
      return;
    }
    if (
      draft.userId &&
      salesPointUsers.length > 0 &&
      !salesPointUsers.some((u) => u.id === draft.userId)
    ) {
      setDraft((p) => ({ ...p, userId: undefined }));
    }
  }, [draft.salesPointId, draft.userId, salesPointUsers]);

  const queryKey = useMemo(
    () => ["sales-report", filters, page, rowsPerPage, sortBy, sortOrder],
    [filters, page, rowsPerPage, sortBy, sortOrder]
  );

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      reportAPI.getSalesReport({
        ...filters,
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...sortParams,
      }),
    enabled: Boolean(filters.startDate && filters.endDate),
    staleTime: 0,
  });

  const applyFilters = () => {
    setPage(0);
    setFilters({
      ...draft,
      branchId: isAdmin ? user?.branchId : draft.branchId || undefined,
    });
  };

  const onSort = (field: SalesReportSortField) =>
    handleSort(field, () => setPage(0));

  const errorMsg =
    error instanceof Error
      ? error.message
      : (error as any)?.response?.data?.message || "Failed to load sales report";

  return (
    <Box>
      {actionMsg && (
        <Alert
          severity={actionMsg.type}
          sx={{ mb: 2 }}
          onClose={() => setActionMsg(null)}
        >
          {actionMsg.text}
        </Alert>
      )}
      <PageHeader
        title="Sales report"
        subtitle={
          isSuperAdmin
            ? "Branch and salesPoint wise sales (all branches)"
            : "Sales for your branch only"
        }
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Sales report" },
        ]}
      />

      <Paper sx={{ p: 1.5, mb: 1.5, border: "1px solid", borderColor: "divider" }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4} md={1.5} sx={{ maxWidth: { sm: 180, md: 160 } }}>
            <FormControl fullWidth size="small">
              <InputLabel>Financial year</InputLabel>
              <Select
                label="Financial year"
                value={draft.fyCode || ""}
                onChange={(e) => {
                  const code = e.target.value || undefined;
                  const fy =
                    (financialYears as FinancialYear[]).find((f) => f.code === code) ||
                    null;
                  const meta = fy
                    ? {
                        startDate: String(fy.startDate).slice(0, 10),
                        endDate: String(fy.endDate).slice(0, 10),
                      }
                    : indiaFyMeta();
                  const today = format(new Date(), "yyyy-MM-dd");
                  setDraft((p) => ({
                    ...p,
                    fyCode: code,
                    startDate: meta.startDate,
                    endDate: today < meta.endDate ? today : meta.endDate,
                  }));
                }}
              >
                {(financialYears as FinancialYear[]).length > 0
                  ? (financialYears as FinancialYear[]).map((fy) => (
                      <MenuItem key={fy.id} value={fy.code}>
                        {formatFinancialYearDisplay(fy)}
                        {fy.isCurrent ? " (current)" : ""}
                      </MenuItem>
                    ))
                  : (
                      <MenuItem value={indiaFyMeta().code}>
                        {indiaFyMeta().display} (current)
                      </MenuItem>
                    )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={1.5} sx={{ maxWidth: { sm: 160, md: 150 } }}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              value={draft.startDate}
              onChange={(e) => setDraft((p) => ({ ...p, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5} sx={{ maxWidth: { sm: 160, md: 150 } }}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              value={draft.endDate}
              onChange={(e) => setDraft((p) => ({ ...p, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {isSuperAdmin && (
            <Grid item xs={12} sm={4} md={2} sx={{ maxWidth: { md: 180 } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={draft.branchId || ""}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      branchId: e.target.value || undefined,
                      salesPointId: undefined,
                      userId: undefined,
                    }))
                  }
                >
                  <MenuItem value="">All branches</MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {isAdmin && user?.branchId && (
            <Grid item xs={12} sm={4} md={2} sx={{ maxWidth: { md: 180 } }}>
              <TextField
                fullWidth
                size="small"
                label="Branch"
                value={
                  branches.find((b) => b.id === user.branchId)?.name ||
                  "Your branch"
                }
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12} sm={4} md={2} sx={{ maxWidth: { md: 180 } }}>
            <FormControl fullWidth size="small">
              <InputLabel>SalesPoint</InputLabel>
              <Select
                label="Sales point"
                value={draft.salesPointId || ""}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    salesPointId: e.target.value || undefined,
                    userId: undefined,
                  }))
                }
              >
                <MenuItem value="">All sales points</MenuItem>
                {branchSalesPoints.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {draft.salesPointId ? (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sold by</InputLabel>
                <Select
                  label="Sold by"
                  value={draft.userId || ""}
                  disabled={loadingSoldBy}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      userId: e.target.value || undefined,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {salesPointUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                      {u.role ? ` · ${u.role}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={applyFilters}
              disabled={isFetching}
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {isLoading && (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton height={80} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton height={300} />
          </Grid>
        </Grid>
      )}

      {!isLoading && data && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip
              size="small"
              label={`${formatYmdLabel(filters.startDate)} – ${formatYmdLabel(filters.endDate)}`}
            />
            {data.branch && (
              <Chip size="small" color="primary" label={`Branch: ${data.branch.name}`} />
            )}
            {data.salesPoint && (
              <Chip size="small" color="secondary" label={`Dept: ${data.salesPoint.name}`} />
            )}
            {filters.userId && (
              <Chip
                size="small"
                variant="outlined"
                label={`Sold by: ${
                  salesPointUsers.find((u) => u.id === filters.userId)?.name ||
                  "Selected user"
                }`}
              />
            )}
            {!data.branch && isSuperAdmin && (
              <Chip size="small" variant="outlined" label="All branches" />
            )}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(5, minmax(0, 1fr))",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {(
              [
                {
                  label: "Total sales",
                  value: String(data.summary.totalSales),
                  accent: "#0284c7",
                  bg: "linear-gradient(160deg, #f0f9ff 0%, #e0f2fe 55%, #bae6fd 100%)",
                },
                {
                  label: "Gross amount",
                  value: formatCurrency(data.summary.totalAmount),
                  accent: "#0d9488",
                  bg: "linear-gradient(160deg, #f0fdfa 0%, #ccfbf1 55%, #99f6e4 100%)",
                },
                {
                  label: "Net amount",
                  value: formatCurrency(data.summary.totalNet),
                  accent: "#16a34a",
                  bg: "linear-gradient(160deg, #f0fdf4 0%, #dcfce7 55%, #bbf7d0 100%)",
                },
                {
                  label: "Tax",
                  value: formatCurrency(data.summary.totalTax),
                  accent: "#d97706",
                  bg: "linear-gradient(160deg, #fffbeb 0%, #fef3c7 55%, #fde68a 100%)",
                },
                {
                  label: "Cancelled sales",
                  value: String(data.cancelledSummary?.totalCancelled || 0),
                  meta: formatCurrency(data.cancelledSummary?.cancelledNet || 0),
                  accent: "#e11d48",
                  bg: "linear-gradient(160deg, #fff1f2 0%, #ffe4e6 55%, #fecdd3 100%)",
                },
              ] as Array<{
                label: string;
                value: string;
                meta?: string;
                accent: string;
                bg: string;
              }>
            ).map((tile) => (
              <Card
                key={tile.label}
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid",
                  borderColor: "transparent",
                  borderRadius: 2.5,
                  background: tile.bg,
                  boxShadow: `inset 4px 0 0 ${tile.accent}`,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: tile.accent, letterSpacing: 0.2 }}
                  >
                    {tile.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ mt: 0.5, color: "#0f172a", lineHeight: 1.2 }}
                  >
                    {tile.value}
                  </Typography>
                  {tile.meta ? (
                    <Typography variant="body2" sx={{ mt: 0.5, color: "#64748b" }}>
                      {tile.meta}
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {isSuperAdmin && !filters.branchId && data.byBranch.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Branch wise
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Branch</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Net</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.byBranch.map((row) => (
                          <TableRow key={row.branchId}>
                            <TableCell>{row.branchName}</TableCell>
                            <TableCell align="right">{row.saleCount}</TableCell>
                            <TableCell align="right">{formatCurrency(row.totalNet)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={isSuperAdmin && !filters.branchId ? 6 : 12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    SalesPoint wise
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SalesPoint</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Net</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.bySalesPoint.map((row) => (
                        <TableRow key={row.salesPointId}>
                          <TableCell>{row.salesPointName}</TableCell>
                          <TableCell>{row.branchName}</TableCell>
                          <TableCell align="right">{row.saleCount}</TableCell>
                          <TableCell align="right">{formatCurrency(row.totalNet)}</TableCell>
                        </TableRow>
                      ))}
                      {data.bySalesPoint.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No sales in range
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Sale transactions
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell label="Date" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Sale #" field="saleNumber" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Net" field="netAmount" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                      <SortableTableCell label="Sales point" field="salesPoint" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Payment" field="paymentMethod" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Status" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Holder" field="holderName" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Sold by" field="soldBy" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <TableCell align="right">Bill</TableCell>
                      {canDeleteSale && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.sales.map((sale) => (
                      <TableRow key={sale.id} hover>
                        <TableCell>
                          {formatIstDateTime(sale.createdAt)}
                        </TableCell>
                        <TableCell>{billSeqDigits(sale.saleNumber)}</TableCell>
                        <TableCell align="right">{formatCurrency(sale.netAmount)}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 96,
                            width: 96,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={sale.salesPoint?.name || undefined}
                        >
                          {sale.salesPoint?.name || "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={paymentMethodLabel(sale.paymentMethod)}
                            sx={paymentMethodChipSx(sale.paymentMethod)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={sale.status}
                            color={
                              sale.status === "cancelled"
                                ? "error"
                                : sale.status === "completed"
                                  ? "success"
                                  : "default"
                            }
                            variant={sale.status === "cancelled" ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell>{sale.cardHolder?.holderName || "—"}</TableCell>
                        <TableCell>{sale.creator?.name || "—"}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View bill">
                            <IconButton
                              size="small"
                              onClick={() => setViewSale(sale)}
                              aria-label={`View bill ${sale.saleNumber}`}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        {canDeleteSale && (
                          <TableCell align="right">
                            {sale.status !== "cancelled" && sale.status !== "refunded" ? (
                              <Tooltip title="Cancel sale">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      setCancelTarget({
                                        id: sale.id,
                                        saleNumber: sale.saleNumber,
                                        netAmount: sale.netAmount,
                                        status: sale.status,
                                      })
                                    }
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {data.sales.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={canDeleteSale ? 10 : 9} align="center" sx={{ py: 4 }}>
                          No completed sales in this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                count={data.pagination.totalRecords}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </CardContent>
          </Card>
        </>
      )}

      <SaleBillDrawer
        open={!!viewSale}
        saleRow={viewSale}
        onClose={() => setViewSale(null)}
      />

      <CancelSaleDialog
        open={!!cancelTarget}
        sale={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onSuccess={() => {
          setCancelTarget(null);
          setActionMsg({ type: "success", text: "Sale cancelled" });
          queryClient.invalidateQueries({ queryKey: ["sales-report"] });
          refetch();
        }}
      />
    </Box>
  );
};

export default SalesReportPage;
